import { useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TypographyWithTooltip,
  useTranslations,
  IMaterialTableProps,
  IUseTableReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IMaterialTableOptions,
} from '@uniqkey-frontend/shared-app';
import PageRouteEnum from '../../../enums/PageRouteEnum';
import { TGetOrganizationsMethod } from '../../../hooks/usePartnerOrganizationsAPI/interfaces';
import { IOrganizationsTableRow } from '../../../hooks/tables/useOrganizationsTable';

interface IOrganizationsTableWithFilterProps extends
  Omit<IUseTableReturn<IOrganizationsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<
    IUseQueryTableFetchReturn<TGetOrganizationsMethod>,
    'data' | 'resetQuery' | 'isFetchedAfterMount'
  >
{
  organizations: IUseQueryTableFetchReturn<TGetOrganizationsMethod>['data'];
  selectedOrganizations: IUseTableReturn<IOrganizationsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  partnerId?: string;
  options?: IMaterialTableOptions<IOrganizationsTableRow>;
}

const COLUMN_TOOLTIP_PROPS = { cursorPointer: true };

const OrganizationsTableWithFilter = (props: IOrganizationsTableWithFilterProps) => {
  const {
    organizations,
    isLoading,
    total,
    localization,
    partnerId,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedOrganizations,
    onRowSelectionChange,
    createColumns,
    options,
  } = props;

  const { t } = useTranslations();
  const navigate = useNavigate();

  const columns = useMemo(() => createColumns([
    {
      title: t('organizationsPage.organization'),
      field: 'name',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.name}
        </TypographyWithTooltip>
      ),
      width: '17%',
    },
    {
      title: t('organizationsPage.region'),
      field: 'region',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.region}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('organizationsPage.country'),
      field: 'country',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.country}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('organizationsPage.vat'),
      field: 'vat',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.vat}
        </TypographyWithTooltip>
      ),
      width: '12%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={COLUMN_TOOLTIP_PROPS}
          lineClamp={5}
          title={t('organizationsPage.committedLicenses.columnTooltip')}
        >
          {t('organizationsPage.committedLicenses')}
        </TypographyWithTooltip>
      ),
      field: 'committedLicenseCount',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.committedLicenseCount}
        </TypographyWithTooltip>
      ),
      width: '13%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={COLUMN_TOOLTIP_PROPS}
          lineClamp={5}
          title={t('organizationsPage.licenses.columnTooltip')}
        >
          {t('organizationsPage.licenses')}
        </TypographyWithTooltip>
      ),
      field: 'licenseCount',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.licenseCount}
        </TypographyWithTooltip>
      ),
      width: '13%',
    },
    {
      title: t('organizationsPage.movedFromPartnerName'),
      field: 'movedFromPartnerName',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.movedFromPartnerName}
        </TypographyWithTooltip>
      ),
      width: '15%',
      sorting: false,
    },
  ]), [createColumns, t]);

  const handleOrganizationRowClick = useCallback<
    NonNullable<IMaterialTableProps<IOrganizationsTableRow>['onRowClick']>
  >((event, organization) => {
    const { organizationId } = organization!;
    if (partnerId) {
      navigate(`${PageRouteEnum.Partners}/${partnerId}/${organizationId}`);
      return;
    }
    navigate(`${PageRouteEnum.Organizations}/${organizationId}`);
  }, [partnerId, navigate]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={organizations}
      isLoading={isLoading}
      options={options}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedOrganizations}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      onRowClick={handleOrganizationRowClick}
      rowSelectByKey="organizationId"
      rowHeight={51}
    />
  );
};

export default memo(OrganizationsTableWithFilter);
